import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useOrg } from '../context/OrgContext';
import { usePermissions } from '../components/PermissionGuard';
import { PageHeader } from '../components/PageHeader';
import * as incidentsApi from '../api/incidents';
import * as exportsApi from '../api/exports';

export function IncidentDetail() {
  const { incidentId } = useParams();
  const navigate = useNavigate();
  const { selectedOrg } = useOrg();
  const { canReadAssets } = usePermissions();
  const [incident, setIncident] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);

  const loadIncident = useCallback(async () => {
    if (!selectedOrg || !canReadAssets || !incidentId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const incidentData = await incidentsApi.getIncident(selectedOrg.id, incidentId);
      setIncident(incidentData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedOrg, canReadAssets, incidentId]);

  useEffect(() => {
    loadIncident();
  }, [loadIncident]);

  const handleExport = async () => {
    if (!selectedOrg || exporting) return;
    setExporting(true);
    try {
      const exportJob = await incidentsApi.exportIncidentSummary(selectedOrg.id, incidentId);
      if (exportJob.status === 'completed' && exportJob.artifactId) {
        const { blob, filename } = await exportsApi.downloadArtifact(selectedOrg.id, exportJob.artifactId);
        exportsApi.triggerDownload(blob, filename);
      }
    } catch (err) {
      console.error('Failed to export:', err);
      setError('Failed to export incident summary');
    } finally {
      setExporting(false);
    }
  };

  if (!canReadAssets) {
    return (
      <div style={styles.container}>
        <div style={styles.accessDenied}>
          <h2>Access Denied</h2>
          <p>You do not have permission to view this incident.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Loading incident...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.error}>{error}</div>
        <button onClick={() => navigate('/uptime')} style={styles.backButton}>
          Back to Uptime
        </button>
      </div>
    );
  }

  if (!incident) {
    return (
      <div style={styles.container}>
        <div style={styles.notFound}>Incident not found</div>
        <button onClick={() => navigate('/uptime')} style={styles.backButton}>
          Back to Uptime
        </button>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <PageHeader
        title={`Incident: ${incident.type}`}
        subtitle={incident.id}
        breadcrumbs={[
          { label: 'Uptime', href: '/uptime' },
          { label: incident.id }
        ]}
      />

      <div style={styles.actions}>
        <button
          onClick={handleExport}
          disabled={exporting}
          style={styles.exportButton}
          data-testid="export-incident-button"
        >
          {exporting ? 'Exporting...' : 'Export Summary'}
        </button>
      </div>

      <div style={styles.grid}>
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>Incident Details</h3>
          <div style={styles.infoRow}>
            <span style={styles.label}>Type:</span>
            <span>{incident.type}</span>
          </div>
          <div style={styles.infoRow}>
            <span style={styles.label}>Status:</span>
            <span style={{
              ...styles.statusBadge,
              background: getStatusColor(incident.status)
            }}>
              {incident.status}
            </span>
          </div>
          <div style={styles.infoRow}>
            <span style={styles.label}>Severity:</span>
            <span style={{
              ...styles.severityBadge,
              background: getSeverityColor(incident.severity)
            }}>
              {incident.severity}
            </span>
          </div>
          <div style={styles.infoRow}>
            <span style={styles.label}>Start Time:</span>
            <span>{new Date(incident.startTime).toLocaleString()}</span>
          </div>
          {incident.endTime && (
            <div style={styles.infoRow}>
              <span style={styles.label}>End Time:</span>
              <span>{new Date(incident.endTime).toLocaleString()}</span>
            </div>
          )}
          {incident.durationMinutes && (
            <div style={styles.infoRow}>
              <span style={styles.label}>Duration:</span>
              <span>{incident.durationMinutes} minutes</span>
            </div>
          )}
        </div>

        <div style={styles.card}>
          <h3 style={styles.cardTitle}>Location</h3>
          <div style={styles.infoRow}>
            <span style={styles.label}>Site:</span>
            <span>{incident.siteName} ({incident.siteId})</span>
          </div>
          <div style={styles.infoRow}>
            <span style={styles.label}>Asset:</span>
            <span>{incident.assetName} ({incident.assetId})</span>
          </div>
          {incident.portId && (
            <div style={styles.infoRow}>
              <span style={styles.label}>Port:</span>
              <span>{incident.portId}</span>
            </div>
          )}
        </div>

        <div style={styles.card}>
          <h3 style={styles.cardTitle}>Description</h3>
          <p style={styles.description}>{incident.description || 'No description provided'}</p>
          {incident.rootCause && (
            <>
              <h4 style={styles.subTitle}>Root Cause</h4>
              <p style={styles.description}>{incident.rootCause}</p>
            </>
          )}
          {incident.resolution && (
            <>
              <h4 style={styles.subTitle}>Resolution</h4>
              <p style={styles.description}>{incident.resolution}</p>
            </>
          )}
        </div>

        <div style={styles.card}>
          <h3 style={styles.cardTitle}>Impact</h3>
          <div style={styles.infoRow}>
            <span style={styles.label}>Affected Sessions:</span>
            <span>{incident.affectedSessions || 0}</span>
          </div>
        </div>
      </div>

      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Notes</h3>
        <div style={styles.notesList} data-testid="incident-notes">
          {incident.notes && incident.notes.length > 0 ? (
            incident.notes.map(note => (
              <div key={note.id} style={styles.noteItem}>
                <div style={styles.noteMeta}>
                  {new Date(note.createdAt).toLocaleString()}
                </div>
                <div style={styles.noteText}>{note.text}</div>
              </div>
            ))
          ) : (
            <div style={styles.emptyState}>No notes</div>
          )}
        </div>
      </div>

      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Attachments</h3>
        <div style={styles.attachmentsList} data-testid="incident-attachments">
          {incident.attachments && incident.attachments.length > 0 ? (
            incident.attachments.map(att => (
              <div key={att.id} style={styles.attachmentItem}>
                <span style={styles.attachmentName}>{att.filename}</span>
                <span style={styles.attachmentSize}>{(att.size / 1024).toFixed(1)} KB</span>
              </div>
            ))
          ) : (
            <div style={styles.emptyState}>No attachments</div>
          )}
        </div>
      </div>
    </div>
  );
}

function getStatusColor(status) {
  const colors = {
    resolved: '#48bb78',
    open: '#f56565',
    investigating: '#ed8936'
  };
  return colors[status] || '#a0aec0';
}

function getSeverityColor(severity) {
  const colors = {
    high: '#fc8181',
    medium: '#f6e05e',
    low: '#68d391'
  };
  return colors[severity] || '#e2e8f0';
}

const styles = {
  container: {
    padding: '2rem',
    maxWidth: '1200px',
    margin: '0 auto'
  },
  accessDenied: {
    padding: '3rem',
    textAlign: 'center',
    background: '#fff',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  },
  loading: {
    padding: '2rem',
    textAlign: 'center',
    color: '#718096'
  },
  error: {
    padding: '1rem',
    background: '#fed7d7',
    color: '#c53030',
    borderRadius: '8px',
    marginBottom: '1rem'
  },
  notFound: {
    padding: '2rem',
    textAlign: 'center',
    color: '#718096'
  },
  backButton: {
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
    background: 'white',
    cursor: 'pointer'
  },
  actions: {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '1.5rem'
  },
  exportButton: {
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    border: '1px solid #3182ce',
    background: '#3182ce',
    color: 'white',
    fontSize: '0.875rem',
    cursor: 'pointer'
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '1.5rem',
    marginBottom: '1.5rem'
  },
  card: {
    background: 'white',
    borderRadius: '8px',
    padding: '1.5rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  },
  cardTitle: {
    margin: '0 0 1rem 0',
    fontSize: '1rem',
    fontWeight: '600',
    color: '#2d3748'
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0.5rem 0',
    borderBottom: '1px solid #f7fafc'
  },
  label: {
    color: '#718096',
    fontSize: '0.875rem'
  },
  statusBadge: {
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.75rem',
    color: 'white',
    fontWeight: '500',
    textTransform: 'capitalize'
  },
  severityBadge: {
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.75rem',
    fontWeight: '500',
    textTransform: 'capitalize'
  },
  description: {
    margin: '0',
    fontSize: '0.875rem',
    color: '#4a5568',
    lineHeight: '1.5'
  },
  subTitle: {
    margin: '1rem 0 0.5rem 0',
    fontSize: '0.875rem',
    fontWeight: '600',
    color: '#4a5568'
  },
  section: {
    background: 'white',
    borderRadius: '8px',
    padding: '1.5rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    marginBottom: '1.5rem'
  },
  sectionTitle: {
    margin: '0 0 1rem 0',
    fontSize: '1rem',
    fontWeight: '600',
    color: '#2d3748'
  },
  notesList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem'
  },
  noteItem: {
    padding: '0.75rem',
    background: '#f7fafc',
    borderRadius: '6px'
  },
  noteMeta: {
    fontSize: '0.75rem',
    color: '#718096',
    marginBottom: '0.25rem'
  },
  noteText: {
    fontSize: '0.875rem',
    color: '#2d3748'
  },
  attachmentsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem'
  },
  attachmentItem: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0.75rem',
    background: '#f7fafc',
    borderRadius: '6px'
  },
  attachmentName: {
    fontSize: '0.875rem',
    color: '#2d3748'
  },
  attachmentSize: {
    fontSize: '0.75rem',
    color: '#718096'
  },
  emptyState: {
    padding: '1rem',
    textAlign: 'center',
    color: '#718096',
    fontSize: '0.875rem'
  }
};
